import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';
import { ArrowLeft, Shield, Cookie, Database, Mail, UserCheck, Clock, Globe } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "October 6, 2026";

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1F3C4F] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-10 h-10 text-[#1F3C4F]" />
              <h1 className="text-4xl font-bold text-slate-900">Privacy Policy</h1>
            </div>
            <p className="text-slate-500">Last Updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg prose-slate max-w-none">
            
            {/* Introduction */}
            <section className="mb-12">
              <p className="text-slate-600">
                SPT Digital Marketing LLC ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website <strong>sptdigitalmarketing.com</strong> or use our services.
              </p>
              <p className="text-slate-600">
                Please read this privacy policy carefully. If you do not agree with the terms of this 
                privacy policy, please do not access the site.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Information We Collect</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-slate-800 mt-6">Personal Information</h3>
              <p className="text-slate-600">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Fill out a contact form</li>
                <li>Request a consultation or quote</li>
                <li>Subscribe to our newsletter</li>
                <li>Engage with us on social media</li>
                <li>Communicate with us via email or phone</li>
              </ul>
              <p className="text-slate-600">This information may include:</p>
              <ul className="text-slate-600 space-y-2">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company name</li>
                <li>Message content</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6">Automatically Collected Information</h3>
              <p className="text-slate-600">
                When you visit our website, we may automatically collect certain information about your 
                device and usage, including:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring website</li>
                <li>Pages viewed and time spent</li>
                <li>Date and time of visit</li>
              </ul>
            </section>

          {/* Cookie Policy */}
          <section className="mb-12 p-6 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="w-6 h-6 text-[#1F3C4F]" />
              <h2 className="text-2xl font-bold text-slate-900 m-0">Cookies & Analytics</h2>
            </div>

            <p className="text-slate-600">
              We use cookies and similar storage to run the site and, with your permission, to understand how
              visitors use it. Our full cookie breakdown — names, lifetimes, and your controls — lives on the{' '}
              <Link to="/cookie-policy" className="text-[#1F3C4F] underline hover:text-black font-medium">
                Cookie Policy
              </Link>{' '}page.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6">Consent-Gated Analytics</h3>
            <p className="text-slate-600">
              No analytics cookies are set and no analytics scripts load until you click{' '}
              <strong>"Accept all"</strong> on our cookie banner. If you select{' '}
              <strong>"Essential only"</strong> or dismiss the banner, Google Analytics is <em>not</em> loaded —
              no network calls to googletagmanager.com, no <code className="text-xs font-mono bg-white px-1 py-0.5 rounded">_ga</code> cookies, nothing.
              This complies with GDPR Art. 6(1)(a), the ePrivacy Directive (Art. 5(3)), and FTC guidance on
              consent-first tracking.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-6">Categories</h3>
            <ul className="text-slate-600 space-y-3">
              <li>
                <strong>Essential (always on):</strong> required for the site to work — remembers your consent
                choice, keeps staff logged in, protects against abuse. Covered by GDPR Art. 5(3) legitimate
                interest; cannot be disabled without breaking the site.
              </li>
              <li>
                <strong>Analytics (opt-in only):</strong> Google Analytics 4 — aggregated page views, session
                duration, and acquisition channel. IP anonymisation is enabled. Loads only after you consent.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-6">Withdrawing Consent</h3>
            <p className="text-slate-600">
              Withdrawing consent is as easy as giving it. Visit the{' '}
              <Link to="/cookie-policy" className="text-[#1F3C4F] underline hover:text-black font-medium">
                Cookie Policy
              </Link>{' '}
              page and click <strong>"Withdraw Analytics Consent"</strong> — we immediately stop the GA tracker and
              clear any <code className="text-xs font-mono bg-white px-1 py-0.5 rounded">_ga</code> /{' '}
              <code className="text-xs font-mono bg-white px-1 py-0.5 rounded">_ga_&lt;ID&gt;</code> cookies from your browser.
            </p>
          </section>

            {/* How We Use Your Information */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">How We Use Your Information</h2>
              </div>
              
              <p className="text-slate-600">We use the information we collect to:</p>
              <ul className="text-slate-600 space-y-2">
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send you information about our services</li>
                <li>Process and fulfill service requests</li>
                <li>Improve our website and services</li>
                <li>Analyze usage trends and website performance</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraudulent or illegal activity</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Information Sharing & Third Parties</h2>
              </div>
              
              <p className="text-slate-600">
                We do not sell, trade, or rent your personal information to third parties. We may share 
                your information in the following circumstances:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>
                  <strong>Service Providers:</strong> We may share information with trusted third-party 
                  service providers who assist us in operating our website and business (e.g., hosting, 
                  analytics, email services).
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose information if required by law or 
                  in response to valid legal requests.
                </li>
                <li>
                  <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of 
                  assets, your information may be transferred.
                </li>
                <li>
                  <strong>With Your Consent:</strong> We may share information with your explicit consent.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6">Third-Party Services We Use</h3>
              <ul className="text-slate-600 space-y-2">
                <li><strong>Google Drive:</strong> Secure file storage for receipts and invoice attachments (business workspace)</li>
                <li><strong>Google Analytics 4:</strong> Aggregated website analytics — loads only after you click "Accept all" on our cookie banner</li>
                <li><strong>MongoDB Atlas:</strong> Encrypted application database</li>
                <li><strong>Cloudflare:</strong> Edge security, DDoS protection, and HTTPS termination</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6">Hosting Platform Disclosure</h3>
              <p className="text-slate-600">
                Our website is hosted on the <strong>Emergent platform</strong>. Emergent may collect basic usage
                and performance data about visits through its analytics provider (PostHog). We do not control this
                collection and do not receive or use this data. Questions about Emergent's data practices can be
                directed to{' '}
                <a href="mailto:support@emergent.sh" className="text-[#1F3C4F] underline hover:text-black font-medium">
                  support@emergent.sh
                </a>.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Data Retention</h2>
              </div>
              
              <p className="text-slate-600">
                We retain your personal information only for as long as necessary to fulfill the purposes 
                outlined in this privacy policy, unless a longer retention period is required or permitted 
                by law.
              </p>
              <p className="text-slate-600">
                Contact form submissions are retained for up to 3 years for business and legal purposes. 
                You may request deletion of your data at any time.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-12 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Your Privacy Rights</h2>
              </div>
              
              <p className="text-slate-600">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mt-6">For All Users</h3>
              <ul className="text-slate-600 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6">For California Residents (CCPA)</h3>
              <p className="text-slate-600">
                Under the California Consumer Privacy Act, you have additional rights including:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to know if personal information is sold or disclosed</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mt-6">For EU/UK Residents (GDPR)</h3>
              <p className="text-slate-600">
                Under the General Data Protection Regulation, you have additional rights including:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Right to data portability</li>
                <li>Right to restrict processing</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent</li>
              </ul>

              <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
                <p className="text-slate-700 font-medium m-0">
                  To exercise any of these rights, please contact us at:{' '}
                  <a href="mailto:info@sptdigitalmarketing.com" className="text-blue-600 hover:underline">
                    info@sptdigitalmarketing.com
                  </a>
                </p>
              </div>
            </section>

            {/* Security */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">Data Security</h2>
              <p className="text-slate-600">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                These measures include:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Encryption of data in transit (HTTPS/SSL)</li>
                <li>Secure data storage with access controls</li>
                <li>Regular security assessments</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="text-slate-600">
                However, no method of transmission over the internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">Children's Privacy</h2>
              <p className="text-slate-600">
                Our website is not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If you are a parent or guardian 
                and believe your child has provided us with personal information, please contact us 
                immediately.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">Changes to This Privacy Policy</h2>
              <p className="text-slate-600">
                We may update this privacy policy from time to time. The updated version will be indicated 
                by an updated "Last Updated" date at the top of this page. We encourage you to review this 
                privacy policy periodically to stay informed about how we are protecting your information.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-12 p-6 bg-slate-100 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Contact Us</h2>
              </div>
              
              <p className="text-slate-600">
                If you have questions or concerns about this privacy policy or our data practices, 
                please contact us:
              </p>
              <div className="mt-4 space-y-2 text-slate-700">
                <p><strong>SPT Digital Marketing LLC</strong></p>
                <p>Email: <a href="mailto:info@sptdigitalmarketing.com" className="text-[#1F3C4F] hover:underline">info@sptdigitalmarketing.com</a></p>
                <p>Website: <a href="https://sptdigitalmarketing.com" className="text-[#1F3C4F] hover:underline">sptdigitalmarketing.com</a></p>
                <p>Location: Southport, NC</p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
